/**
 * Nouveautés et retours (GDD §27).
 *
 * Deux écrans qui vont ensemble : ce que le jeu a changé, et ce que les joueurs
 * en pensent. Rangés sous l'onglet Chat, le seul du jeu qui parle des gens —
 * ajouter un septième onglet à la barre l'aurait rendue illisible sur téléphone.
 */
import type { Ctx } from "../contexte.ts"
import { sousOnglets } from "../contexte.ts"
import type { Nouveaute } from "../../net/news.ts"
import type { Retour } from "../../net/retours.ts"
import { esc } from "../fmt.ts"
import { pseudoLocal } from "../../net/compte.ts"

export const SOUS_SOCIAL: [string, string][] = [
  ["salon", "Salon"], ["news", "Nouveautés"], ["retours", "Bugs & idées"],
]

export interface EtatSocial {
  news: Nouveaute[] | null
  retours: Retour[] | null
  /** l'onglet « Bugs & idées » est-il en train d'envoyer ? */
  envoi: boolean
  erreur: string | null
  /** « bug » ou « idee » : ce que le formulaire s'apprête à envoyer */
  type: "bug" | "idee"
  merci: boolean
  /** ce compte peut-il répondre aux retours ? le serveur le dit, et le revérifie */
  admin: boolean
  /** le retour dont le volet de réponse est ouvert */
  repond: number | null
}

export const socialVide = (): EtatSocial =>
  ({ news: null, retours: null, envoi: false, erreur: null, type: "bug", merci: false,
    admin: false, repond: null })

/** La barre de sous-onglets, avec la pastille des nouveautés non lues. */
export function barreSocial(c: Ctx, nonLues: number): string {
  const items: [string, string][] = SOUS_SOCIAL.map(([k, lib]) =>
    [k, k === "news" && nonLues > 0 ? `${lib} <b class="pastille">${nonLues}</b>` : lib])
  return sousOnglets(c.sousSocial, items, "soussocial")
}

/**
 * Les nouveautés.
 *
 * Le gras est le seul balisage accepté, et il est produit ici : le texte vient
 * d'un fichier que j'écris, mais le passer tel quel dans le HTML serait prendre
 * l'habitude de faire confiance à une source — et cette habitude se perd mal.
 */
export function vueNews(c: Ctx): string {
  const n = c.social.news
  if (n === null) return `<section class="carte"><p class="aide">Chargement…</p></section>`
  if (n.length === 0) {
    return `<section class="carte"><p class="aide">Rien à signaler pour l'instant.</p></section>`
  }
  return `<section class="carte news">
    <h2>Nouveautés</h2>
    ${n.map((x: Nouveaute) => `
      <article class="nouvelle">
        <h3>${esc(x.titre)}</h3>
        <time>${esc(dateLisible(x.date))}</time>
        <ul>${x.lignes.map((l: string) => `<li>${gras(l)}</li>`).join("")}</ul>
      </article>`).join("")}
  </section>`
}

/** `**ceci**` devient du gras. Tout le reste est échappé. */
function gras(texte: string): string {
  return esc(texte).replace(/\*\*([^*]+)\*\*/g, "<b>$1</b>")
}

function dateLisible(iso: string): string {
  const [a, m, j] = iso.split("-")
  const mois = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet",
    "août", "septembre", "octobre", "novembre", "décembre"]
  return `${Number(j)} ${mois[Number(m) - 1] ?? ""} ${a}`
}

const LIBELLE_ETAT: Record<string, string> = {
  ouvert: "reçu", vu: "lu", fait: "fait", refuse: "pas retenu",
}

/**
 * Les retours.
 *
 * On montre ceux des autres, et pas seulement le formulaire : un joueur qui voit
 * son bug déjà signalé ne le signale pas une seconde fois, et un joueur qui voit
 * qu'on répond a envie d'écrire.
 */
export function vueRetours(c: Ctx): string {
  const s = c.social
  const moi = pseudoLocal()
  return `
  <section class="carte">
    <h2>Un bug, une idée ?</h2>
    <p class="aide">Tout est lu. Pas besoin d'avoir sécurisé ta partie pour écrire —
      mais avec un pseudonyme, je peux te répondre.</p>
    ${s.merci ? `<p class="verdict ok">Merci, c'est noté.</p>` : ""}
    ${s.erreur ? `<p class="secur alerte">${esc(s.erreur)}</p>` : ""}
    <form id="formRetour" class="formRetour">
      <nav class="filtres puces">
        <button type="button" data-typeretour="bug" aria-current="${s.type === "bug"}">Un bug</button>
        <button type="button" data-typeretour="idee" aria-current="${s.type === "idee"}">Une idée</button>
      </nav>
      <textarea name="texte" rows="4" maxlength="600"
        placeholder="${s.type === "bug"
          ? "Ce que tu faisais, ce que tu attendais, ce qui s'est passé."
          : "Ce qui te manque, ou ce qui t'agace."}"></textarea>
      <button class="primaire" type="submit" ${s.envoi ? "disabled" : ""}>${
        s.envoi ? "Envoi…" : "Envoyer"}</button>
    </form>
  </section>

  <section class="carte">
    <h2>Déjà remontés <em>${s.retours?.length ?? 0}</em></h2>
    ${s.retours === null
      ? `<p class="aide">Chargement…</p>`
      : s.retours.length === 0
        ? `<p class="aide">Personne n'a encore rien signalé. À toi de commencer.</p>`
        : `<div class="retours">${s.retours.map((r: Retour) => `
            <article class="retour ${r.type} ${r.etat}">
              <div class="tete">
                <span class="type">${r.type === "bug" ? "Bug" : "Idée"}</span>
                <span class="qui">${r.pseudo ? esc(r.pseudo) : "anonyme"}${
                  r.pseudo && r.pseudo === moi ? " <em>(toi)</em>" : ""}</span>
                <span class="etat ${r.etat}">${LIBELLE_ETAT[r.etat] ?? r.etat}</span>
              </div>
              <p class="texte">${esc(r.texte)}</p>
              ${r.reponse ? `<p class="reponse">${esc(r.reponse)}</p>` : ""}
              ${s.admin ? volet(s, r) : ""}
            </article>`).join("")}</div>`}
  </section>`
}

/**
 * Les commandes de réponse, réservées à l'auteur du jeu.
 *
 * Elles vivent **dans le retour lui-même**, pas dans un écran d'administration
 * séparé : c'est en lisant le message qu'on sait quoi répondre, et un
 * back-office à part serait un deuxième endroit à tenir pour trois boutons.
 *
 * Le volet ne s'ouvre qu'au clic : douze retours affichant chacun un champ de
 * texte et quatre boutons feraient de l'écran une console, alors qu'il est
 * d'abord la liste que lisent les joueurs.
 */
function volet(s: EtatSocial, r: Retour): string {
  if (s.repond !== r.id) {
    return `<div class="admin"><button class="mini" data-repondre="${r.id}">Répondre</button></div>`
  }
  return `<div class="admin ouvert">
    <textarea id="reponseRetour" maxlength="600" rows="2"
      placeholder="Ce que verra ${r.pseudo ? esc(r.pseudo) : "l'auteur"}…">${esc(r.reponse ?? "")}</textarea>
    <div class="etats">${ETATS.map(([k, lib]) =>
      `<button class="mini ${k === r.etat ? "primaire" : ""}" data-etatretour="${r.id}" data-etat="${k}"
        title="Marquer « ${lib} » et enregistrer la réponse">${lib}</button>`).join("")}</div>
    <button class="mini" data-repondre="0">Annuler</button>
  </div>`
}

/** Les quatre états, dans l'ordre où l'on s'en sert. */
const ETATS: [cle: string, libelle: string][] = [
  ["vu", "lu"], ["fait", "fait"], ["refuse", "pas retenu"], ["ouvert", "rouvrir"],
]
